import React from 'react';
import styles from './selector.module.scss'
import CheckIcon from '@mui/icons-material/Check';
import {AutoCompleteItemValue} from './types';
import useAutocomplete from '@mui/material/useAutocomplete';
import classNames from 'classnames';
import {IntlShape} from 'react-intl';
import {Lx} from '../../../../i18n/consts';
import Listbox from '../../listbox'
import Popper from '@mui/material/Popper';
import {Tag} from '../../tag';

const DEFAULT_SELECTOR_WIDTH = 266;
const TAG_WIDTH = 90;
const TAGS_GAP = 8;
const COUNTER_MIN_WIDTH = 30;
const INPUT_HORIZONTAL_OFFSET = 24;

const getTagsWidth = (count: number): number => {
    if (count <= 0) {
        return 0;
    }

    return count * TAG_WIDTH + Math.max(0, count - 1) * TAGS_GAP;
};

const getVisibleTagCount = (totalCount: number, availableWidth: number): number => {
    if (totalCount <= 0) {
        return 0;
    }

    if (getTagsWidth(totalCount) <= availableWidth) {
        return totalCount;
    }

    // Keep space for the +N counter when not all tags fit.
    const counterWidth = COUNTER_MIN_WIDTH + TAGS_GAP;

    for (let count = totalCount - 1; count >= 1; count -= 1) {
        if (getTagsWidth(count) + counterWidth <= availableWidth) {
            return count;
        }
    }

    return 1;
};

interface Props {
    name: string;
    label: string;
    items: AutoCompleteItemValue[];
    setSelectedValues: (values: AutoCompleteItemValue[]) => void;
    selected?: AutoCompleteItemValue[];
    id?: string;
    intl?: IntlShape;
    maxWidth?: number;
    groups?: boolean;
    disabled?: boolean;
}

type SelectorCssVars = Record<
    '--selector-tag-width' | '--selector-tags-gap' | '--selector-counter-min-width' | '--selector-input-horizontal-offset',
    string
>;

const Selector: React.FC<Props> = (props) => {
    const {name, label, id, items, setSelectedValues, selected, intl, maxWidth, disabled} = props;
    const [anchorWidth, setAnchorWidth] = React.useState<number>(0);
    const [anchorElement, setAnchorElement] = React.useState<HTMLDivElement | null>(null);
    const resolveLabel = (key?: string): string => {
        if (!key) {
            return '';
        }

        return intl ? intl.formatMessage({id: key}) : key;
    };
    const entityLabel = resolveLabel(name) || resolveLabel(label) || '';
    const placeholderText = intl
        ? intl.formatMessage(
            {id: Lx.General.ADD_ENTITIES, defaultMessage: 'Add {s}'},
            {
                s: (entityLabel || resolveLabel(Lx.General.EMPLOYEES) || 'entities').toLowerCase(),
            }
        )
        : `Add ${(entityLabel || 'entities').toLowerCase()}`;
    const clearLabel = intl?.formatMessage({id: Lx.General.CLEAR, defaultMessage: 'Clear field'}) ?? 'Clear field';
    const defaultValues: AutoCompleteItemValue[] = (selected ?? [])
        .map(s => items.find((i) => i.id === s.id))
        .filter((item): item is AutoCompleteItemValue => item !== undefined);
    const {
        getRootProps,
        getInputProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        focused,
        getClearProps,
        popupOpen,
        value,
        getTagProps,
    } = useAutocomplete({
        id: id ? id : 'autocomplete',
        value: defaultValues,
        multiple: true,
        options: items,
        getOptionLabel: (option) => (option as AutoCompleteItemValue).name,
        getOptionKey: (option) => (option as AutoCompleteItemValue).id,
        filterOptions: (options, state) => {
            const filter = options.
                filter((x) => x.name.toLowerCase().includes(state.inputValue.toLowerCase()));
            return filter;
        },
        onChange: (e, value) => {
            setSelectedValues((value as AutoCompleteItemValue[]))
        },
        openOnFocus: true,
        freeSolo: true,
        disableCloseOnSelect: true,
        disabled: disabled === true
    });

    const selectedValues = (value as AutoCompleteItemValue[]).filter((x): x is AutoCompleteItemValue => x !== undefined);

    const handleAnchorRef = React.useCallback((node: HTMLDivElement | null) => {
        setAnchorElement(node);
        setAnchorWidth(node?.clientWidth ?? 0);
    }, []);

    React.useEffect(() => {
        if (!anchorElement) {
            setAnchorWidth(0);
            return;
        }

        const updateWidth = () => {
            setAnchorWidth(anchorElement.clientWidth);
        };

        updateWidth();

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => updateWidth());
            observer.observe(anchorElement);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [anchorElement]);

    // calculations to know how much space to leave for +N
    const { valueToRender, leftValues } = React.useMemo(() => {
        const resolvedWidth = anchorWidth || maxWidth || DEFAULT_SELECTOR_WIDTH;
        const availableWidth = Math.max(resolvedWidth - INPUT_HORIZONTAL_OFFSET, TAG_WIDTH);
        const visibleCount = getVisibleTagCount(selectedValues.length, availableWidth);
        const nextValueToRender = selectedValues.slice(0, visibleCount);

        return {
            valueToRender: nextValueToRender,
            leftValues: selectedValues.length - nextValueToRender.length
        };
    }, [anchorWidth, maxWidth, selectedValues]);

    const shouldShowClearButton = selectedValues.length > 0;

    const selectorStyle = React.useMemo<React.CSSProperties & SelectorCssVars>(() => ({
        ...(maxWidth ? { maxWidth } : {}),
        '--selector-tag-width': `${TAG_WIDTH}px`,
        '--selector-tags-gap': `${TAGS_GAP}px`,
        '--selector-counter-min-width': `${COUNTER_MIN_WIDTH}px`,
        '--selector-input-horizontal-offset': `${INPUT_HORIZONTAL_OFFSET}px`,
    }), [maxWidth]);

    return (<div style={selectorStyle} className={classNames({
        [styles.selector]: true,
        [styles.active]: selectedValues.length > 0
    })}>
        <div {...getRootProps()}>
            <div ref={handleAnchorRef}
                className={classNames({
                    [styles.inputWrapper]: true,
                    [styles.wrapperFocus]: focused,
                    [styles.wrapperActive]: selectedValues.length > 0,
                })}
                style={selectorStyle}>
                <div className={styles.contentWrapper}>
                    <div className={classNames({
                        [styles.tagsWrapper]: true,
                        [styles.active]: selectedValues.length > 0,
                    })}>
                        <>
                            {valueToRender.map((option: AutoCompleteItemValue, index: number) => {
                                const {key, ...tagProps} = getTagProps({index});
                                return (
                                    <Tag
                                        label={option.name}
                                        title={option.name}
                                        key={key}
                                        style={{maxWidth: 'var(--selector-tag-width)'}}
                                        {...tagProps}
                                    />
                                );
                            })}
                            {
                                selectedValues.length > valueToRender.length && <span className={styles.counter}>+{leftValues}</span>
                            }
                        </>
                    </div>
                    <input
                        {...getInputProps()}
                        placeholder={placeholderText}
                        aria-label={placeholderText}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
        {
            !disabled && anchorElement && (<Popper open={popupOpen} anchorEl={anchorElement} sx={{zIndex: 9999}} placement={'bottom-start'}>
                {groupedOptions.length > 0 ? (
                    <Listbox className={styles.listBox} {...getListboxProps()} sx={{minWidth: anchorWidth || 0}}>
                        {
                            (groupedOptions as AutoCompleteItemValue[]).map((option, index) => {
                                const { key, ...optionProps } = getOptionProps({option, index});
                                return (
                                    <li {...optionProps} key={key}>
                                        <span>{option.name}</span>
                                        <CheckIcon fontSize="small"/>
                                    </li>);
                            })
                        }
                    </Listbox>
                ) : null}
            </Popper>)
        }
        {
            shouldShowClearButton && !disabled && (
                <button type={'button'} className={styles.clearFieldButton} {...getClearProps()}>
                    {clearLabel}
                </button>
            )
        }
    </div>);
}

export default React.memo(Selector);
